import React, { useEffect, useMemo } from 'react';

import { Row, Col } from 'antd';

import { useFormikContext, Field } from 'formik';
import { SelectFormField } from 'v2/components/Shared/FormFields/SelectFormField';
import { TextFormField } from 'v2/components/Shared/FormFields/TextFormField';
import { CurrencyId } from 'v2/interfaces/financial';
import type { IBanklist, INewAccount, IOperationList } from 'v2/interfaces/financial';

const currencies = [
  {
    value: CurrencyId.BRL,
    label: 'R$'
  },
  {
    value: CurrencyId.USD,
    label: '$'
  }
];

const accounts = [
  {
    value: 'CC',
    label: 'Conta Corrente'
  },
  {
    value: 'CP',
    label: 'Conta Poupança'
  }
];

interface IProps {
  banks: IBanklist[];
  operations: IOperationList[];
  isForeign: boolean;
  isLoadingBanks: boolean;
  isLoadingOperations: boolean;
  isRefundAccount?: boolean;
}

const NewAccountForm = ({
  banks,
  operations,
  isLoadingBanks,
  isLoadingOperations,
  isForeign,
  isRefundAccount
}: IProps) => {
  const { values, setFieldValue } = useFormikContext<INewAccount>();

  const isBRLSelected = values.currency === CurrencyId.BRL;
  const isUSDSelected = values.currency === CurrencyId.USD;
  const caixaEconomicaId = '104';

  const pathName = window.location.pathname;
  const isRefundAccountSelected = isRefundAccount || pathName.includes('bank-account-refund');

  const currencyOptions = useMemo(
    () =>
      currencies.map(option => ({
        ...option,
        disabled: (option.value === CurrencyId.USD && !isForeign) || (option.value === CurrencyId.BRL && isForeign)
      })),
    [isForeign]
  );

  const bankOptions = useMemo(() => banks?.map(bank => ({ label: bank.value, value: bank.id })) ?? [], [banks]);

  const operationOptions = useMemo(
    () =>
      operations?.map(operation => ({ label: `${operation.id} - ${operation.operation}`, value: operation.id })) ?? [],
    [operations]
  );

  useEffect(() => {
    setFieldValue('currency', isForeign ? CurrencyId.USD : CurrencyId.BRL);
  }, [isForeign, setFieldValue]);

  return (
    <Row gutter={[16, 16]}>
      <Col xs={24} sm={8}>
        <Field name='currency' label='Moeda' fullWidth options={currencyOptions} component={SelectFormField} />
      </Col>

      {isBRLSelected && (
        <Col xs={24} sm={16}>
          <Field name='type' label='Tipo de conta' fullWidth options={accounts} component={SelectFormField} />
        </Col>
      )}

      <Col xs={24} sm={isBRLSelected ? 24 : 16}>
        <Field
          name='name'
          label='Correntista'
          placeholder='Nome do correntista'
          helperText={
            !isRefundAccountSelected
              ? '* Só é permitido o cadastro de conta pertencente ao responsável pelo cadastro na Eduzz'
              : ''
          }
          component={TextFormField}
          disabled={!isRefundAccountSelected}
          fullWidth
        />
      </Col>

      <Col span={24}>
        {isBRLSelected && (
          <Field
            name='document_number'
            label='Documento de identificação'
            placeHolder='CPF/CNPJ'
            fullWidth
            mask='document'
            component={TextFormField}
            inputProps={{
              maxLength: 25
            }}
          />
        )}
        {isUSDSelected && (
          <Field
            name='document_number'
            label='FEI/EIN Number'
            placeHolder='FEI/EIN'
            component={TextFormField}
            fullWidth
            inputProps={{
              maxLength: 25
            }}
          />
        )}
      </Col>

      <Col span={24}>
        <Field
          name='bank_id'
          label='Banco'
          fullWidth
          loading={isLoadingBanks}
          options={bankOptions}
          placeholder='Selecione um banco'
          component={SelectFormField}
        />
      </Col>

      {values.bank_id === caixaEconomicaId && (
        <Col span={24}>
          <Field
            name='operation_id'
            label='Operações Caixa Econômica'
            fullWidth
            loading={isLoadingOperations}
            options={operationOptions}
            placeholder='Selecione uma Operação'
            component={SelectFormField}
          />
        </Col>
      )}

      <Col xs={24} sm={isBRLSelected ? 8 : 12}>
        <Field
          name='agency'
          label={isBRLSelected ? 'Agência' : 'ABA Routing Number'}
          fullWidth
          mask='numberandzero'
          component={TextFormField}
          inputProps={{
            maxLength: 20
          }}
        />
      </Col>

      {isBRLSelected && (
        <Col xs={24} sm={4}>
          <Field
            name='agency_digit'
            label='Dígito'
            fullWidth
            component={TextFormField}
            mask='singledigit'
            inputProps={{
              maxLength: 1
            }}
          />
        </Col>
      )}

      <Col xs={24} sm={isBRLSelected ? 8 : 12}>
        <Field
          name='account'
          label={isBRLSelected ? 'Conta' : 'Account Number'}
          component={TextFormField}
          mask='numberandzero'
          fullWidth
          inputProps={{
            maxLength: 20
          }}
        />
      </Col>

      {isBRLSelected && (
        <Col xs={24} sm={4}>
          <Field
            name='account_digit'
            label='Dígito'
            fullWidth
            component={TextFormField}
            mask={values.bank_id === '001' ? 'bankaccountdigit' : 'numberandzero'}
            inputProps={{
              maxLength: 1
            }}
          />
        </Col>
      )}

      {isUSDSelected && (
        <Col span={24}>
          <Field
            name='notes'
            label='Observações'
            fullWidth
            component={TextFormField}
            inputProps={{
              maxLength: 255
            }}
          />
        </Col>
      )}
    </Row>
  );
};

export default NewAccountForm;
